import { z } from 'zod'
import { UUIDTypes } from 'uuid'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Flex, Group, Modal, Paper, Stack, Text } from '@mantine/core'
import { OfferForm } from '@shared/components/offers/offer-form/offer-form'
import { OfferDecisions } from '@shared/components/offers/offer-types'
import { useForm, zodResolver } from '@mantine/form'
import { OfferFormDecisionContext } from '@shared/components/offers/offer-form-decision-context'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertCircle, IconCircleCheck, IconMailForward } from '@tabler/icons-react'
import { OfferDto, OfferStatus, WoolLotOfferDto } from '@grower-api-types'
import { LoomOrGrowerMutation } from '@shared/types'
import { UseMutationResult } from '@tanstack/react-query'
import { ClientError } from 'graphql-request'

type OfferFormDecideProps = {
  offer: OfferDto
  brand: string
  submitOfferMutation?: UseMutationResult<
    LoomOrGrowerMutation,
    ClientError,
    Record<string, any>,
    unknown
  >
}

export function OfferFormDecide({ offer, brand, submitOfferMutation }: OfferFormDecideProps) {
  const { offerId } = useParams()
  const [statusMessage, setStatusMessage] = useState<React.ReactNode>(<></>)
  const [submitModalState, { open: openSubmitModal, close: closeSubmitModal }] =
    useDisclosure(false)

  // Offer form
  const formSchema = z.object({
    decisions: z.array(
      z.object({
        woolLotOfferId: z.string(),
        accepted: z.boolean({
          required_error: 'A decision is required for this wool lot',
        }),
      })
    ),
  })

  const form = useForm<OfferDecisions>({
    mode: 'uncontrolled',
    validate: zodResolver(formSchema),
  })

  /**
   * Loads the wool lots into the form by initializing it with a set of decisions.
   * Each decision corresponds to a wool lot offer and includes its ID and an undefined acceptance status.
   *
   * @param woolLots - An array of `WoolLotOfferDto` objects representing the wool lots to be loaded into the form.
   */
  const loadOfferWoolLotsIntoForm = (woolLots: WoolLotOfferDto[]) => {
    const decisions: OfferDecisions = {
      decisions:
        woolLots.map((lot) => ({
          woolLotOfferId: lot.id,
          accepted: undefined,
        })) || [],
    }
    form.initialize(decisions)
    updateStatusMessage()
  }

  useEffect(() => {
    if (offer.lines) {
      loadOfferWoolLotsIntoForm(offer.lines)
    }
  }, [offer])

  /**
   * Updates the decision for a specific wool lot in the form's state.
   *
   * @param woolLotOfferId - The unique identifier of the wool lot for which the decision is being updated.
   * @param decision - A boolean indicating the decision for the wool lot (`true` for accept, `false`
   * for decline).
   *
   * The function updates the `decisions` array in the form's state by mapping through the existing
   * decisions and modifying the decision object that matches the provided `woolLotOfferId`.
   *
   * NOTE: Because we are having to override the entire value of the decisions, the error values
   * for decisions are wiped. Ideally the decisions were in an object of ID to decision pairs, which
   * would avoid the need to replace the entire array.
   */
  const updateDecision = (woolLotOfferId: UUIDTypes, decision: boolean) => {
    form.setValues({
      decisions: form.getValues().decisions.map((decisionObject) =>
        decisionObject.woolLotOfferId === woolLotOfferId
          ? {
              ...decisionObject,
              accepted: decision,
            }
          : decisionObject
      ),
    })
    updateStatusMessage()
  }

  function updateStatusMessage() {
    const undecidedLots: number = form
      .getValues()
      .decisions.filter((decision) => decision.accepted === undefined).length
    if (undecidedLots > 0) {
      setStatusMessage(
        <Group gap='xs'>
          <IconAlertCircle color='var(--mantine-color-yellow-4)' />
          {undecidedLots === 1 ? (
            <Text>1 wool line needs a decision before submission</Text>
          ) : (
            <Text>{undecidedLots} wool lines need decisions before submission</Text>
          )}
        </Group>
      )
    } else {
      setStatusMessage(
        <Group gap='xs'>
          <IconCircleCheck color='var(--mantine-color-green-4)' />
          <Text>All lines have a decision, ready to submit</Text>
        </Group>
      )
    }
  }

  /**
   * Submits the decisions made on the wool lot offers.
   *
   * This function retrieves the current form values using `form.getValues()`
   * and triggers the `submitOfferMutation` to send the offer decisions to the server.
   */
  const submitOfferDecisions = async () => {
    const formValues = form.getValues()
    submitOfferMutation
      ?.mutateAsync({
        offerId,
        woolLotOfferDecisions: formValues.decisions,
      })
      .catch(() => {
        // Handle error silently, as the error will be shown in the mutation's onError handler
      })
      .finally(() => {
        closeSubmitModal()
      })
  }

  // If the offer is not under offer, we don't show the form
  if (offer.status !== OfferStatus.UnderOffer) {
    return <></>
  }

  return (
    <Stack>
      <Text>
        Please read the following offer, and either accept or decline each line on the offer, then
        click the 'Submit' button at the bottom of this page.
      </Text>

      <OfferFormDecisionContext.Provider value={{ form, decisionCallback: updateDecision }}>
        <OfferForm
          readOnly={false}
          brand={brand}
          offer={{
            ...offer,
          }}
          sentDateTime={offer.created}
        />
        <Paper pos='sticky' bottom={10} withBorder p='md' m='xl' shadow='sm'>
          <form onSubmit={form.onSubmit(() => openSubmitModal())} data-testid='offer-decision-form'>
            <Group justify='space-between' gap='xs'>
              {statusMessage}
              <Button type='submit'>Submit offer decisions</Button>
            </Group>
          </form>
        </Paper>
      </OfferFormDecisionContext.Provider>

      <Modal
        data-testid='send-offer-modal'
        title={
          <Flex justify='flex-start' align='center'>
            <IconMailForward size={28} color='var(--mantine-color-gray-4)' />{' '}
            <Text fw={500} size='lg' ml='sm'>
              Submit offer decisions
            </Text>
          </Flex>
        }
        opened={submitModalState}
        onClose={closeSubmitModal}
        centered
        closeButtonProps={{ 'aria-label': 'Close modal' }}
        returnFocus={false}
      >
        Are you sure you want to submit your decisions for this offer?
        <Group justify='flex-end' mt='md' gap='xs'>
          <Button variant='transparent' onClick={closeSubmitModal}>
            No
          </Button>
          <Button onClick={() => submitOfferDecisions()} loading={submitOfferMutation?.isPending}>
            Yes, submit this offer
          </Button>
        </Group>
      </Modal>
    </Stack>
  )
}
